import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
    Alert,
    CircularProgress,
    Avatar,
    Grow
} from '@mui/material';
import {
    Business,
    Add,
    Edit,
    Delete,
    LocationOn,
    Email,
    Refresh
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../App';

function SubOfficeManagement() {
    const { token } = useAuth();
    const [offices, setOffices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingOffice, setEditingOffice] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        location: '',
        description: '',
        contact_email: ''
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const fetchOffices = async () => {
        setLoading(true);
        try {
            const response = await axios.get('/api/v1/sub-office/', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setOffices(response.data);
        } catch (err) {
            setError('Failed to fetch sub offices.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (token) fetchOffices();
    }, [token]);

    const handleOpenDialog = (office = null) => {
        if (office) {
            setEditingOffice(office);
            setFormData({
                name: office.name,
                location: office.location || '',
                description: office.description || '',
                contact_email: office.contact_email || ''
            });
        } else {
            setEditingOffice(null);
            setFormData({
                name: '',
                location: '',
                description: '',
                contact_email: ''
            });
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingOffice(null);
    };

    const handleSubmit = async () => {
        if (!formData.name) {
            setError('Name is required.');
            return;
        }

        setLoading(true);
        try {
            const headers = { Authorization: `Bearer ${token}` };

            if (editingOffice) {
                await axios.patch(`/api/v1/sub-office/${editingOffice.id}`, formData, { headers });
                setSuccess('Sub office updated successfully.');
            } else {
                await axios.post('/api/v1/sub-office/', formData, { headers });
                setSuccess('Sub office created successfully.');
            }
            handleCloseDialog();
            fetchOffices();
        } catch (err) {
            setError(err.response?.data?.detail || 'Operation failed.');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this sub office?')) return;

        setLoading(true);
        try {
            await axios.delete(`/api/v1/sub-office/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSuccess('Sub office deleted.');
            fetchOffices();
        } catch (err) {
            setError('Failed to delete sub office.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 900, color: 'text.primary', letterSpacing: -1, display: 'flex', alignItems: 'center', gap: 2 }}>
                        Operational Hubs
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        Manage corporate nodes and branch distribution
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => handleOpenDialog()}
                    sx={{
                        fontWeight: 900,
                        borderRadius: 3,
                        px: 3,
                        py: 1,
                        background: 'linear-gradient(45deg, #00B4DB, #0083B0)',
                        boxShadow: '0 8px 16px rgba(0, 180, 219, 0.2)'
                    }}
                >
                    Create Branch
                </Button>
            </Box>

            <Grow in>
                <TableContainer component={Paper} className="glass-card" sx={{ borderRadius: 4, overflow: 'hidden' }}>
                    <Table>
                        <TableHead sx={{ bgcolor: 'rgba(0, 180, 219, 0.05)' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 800 }}>Branch Name</TableCell>
                                <TableCell sx={{ fontWeight: 800 }}>Primary Location</TableCell>
                                <TableCell sx={{ fontWeight: 800 }}>Node Contact</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 800 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {offices.map((office) => (
                                <TableRow key={office.id} sx={{ '&:hover': { bgcolor: 'rgba(0, 180, 219, 0.02)' }, transition: 'background 0.2s' }}>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Avatar sx={{ bgcolor: 'primary.light', width: 32, height: 32 }}>
                                                <Business sx={{ fontSize: 18 }} />
                                            </Avatar>
                                            <Typography variant="body2" sx={{ fontWeight: 800 }}>{office.name}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                                            <LocationOn sx={{ fontSize: 16, mr: 1, color: 'primary.main' }} />
                                            <Typography variant="caption" sx={{ fontWeight: 700 }}>{office.location || 'Undefined'}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                                            <Email sx={{ fontSize: 16, mr: 1, color: 'primary.main' }} />
                                            <Typography variant="caption" sx={{ fontWeight: 700 }}>{office.contact_email || 'N/A'}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" sx={{ color: 'primary.main', bgcolor: 'rgba(0, 180, 219, 0.05)', mr: 1 }} onClick={() => handleOpenDialog(office)}>
                                            <Edit fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" sx={{ color: 'error.main', bgcolor: 'rgba(244, 67, 54, 0.05)' }} onClick={() => handleDelete(office.id)}>
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {offices.length === 0 && !loading && (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                                        <Business sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.2, mb: 1 }} />
                                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                            No operational hubs registered in the directory.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grow>

            <Dialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                PaperProps={{
                    sx: { borderRadius: 5, p: 2, backdropFilter: 'blur(22px)' }
                }}
            >
                <DialogTitle sx={{ fontWeight: 900, fontSize: '1.5rem' }}>
                    {editingOffice ? 'Update Hub Parameters' : 'Provision New Branch'}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontWeight: 600 }}>
                        Configure the strategic identity and contact layer for this node.
                    </Typography>
                    <TextField
                        fullWidth
                        label="Hub Name"
                        margin="normal"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                    />
                    <TextField
                        fullWidth
                        label="Strategic Location"
                        margin="normal"
                        value={formData.location}
                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                    />
                    <TextField
                        fullWidth
                        label="Primary Node Contact"
                        margin="normal"
                        value={formData.contact_email}
                        onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                    />
                    <TextField
                        fullWidth
                        label="Mission Description"
                        margin="normal"
                        multiline
                        rows={3}
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={handleCloseDialog} sx={{ fontWeight: 700, color: 'text.secondary' }}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" sx={{ borderRadius: 3, px: 4, fontWeight: 900 }}>
                        {editingOffice ? 'Synchronize' : 'Propagate Hub'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')}>
                <Alert severity="error" sx={{ borderRadius: 3, fontWeight: 700 }}>{error}</Alert>
            </Snackbar>
            <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess('')}>
                <Alert severity="success" sx={{ borderRadius: 3, fontWeight: 700 }}>{success}</Alert>
            </Snackbar>
        </Box>
    );
}

export default SubOfficeManagement;
